import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import MoreVert from '@mui/icons-material/MoreVert';
import ViewModule from '@mui/icons-material/ViewModule';
import TableChart from '@mui/icons-material/TableChart';
import { useSnackbar } from 'notistack';
import { fetchAnnouncementEvents, deleteAnnouncementEvent } from '../../store/slices/announcementEventSlice';
import { getStatusLabel, getTypeLabel, formatDateFr, computeStatus } from '../../utils/announcementEventUtils';
import { DashboardPageSkeleton } from '../../components/Dashboard/DashboardPageSkeleton';

const CARD_VIEW = 'cards';
const TABLE_VIEW = 'table';

function getStatusColor(status) {
  if (status === 'finished') return 'default';
  if (status === 'ongoing') return 'info';
  return 'success';
}

function AnnouncementEventListContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { list: items, loading } = useSelector((s) => s.announcementEvent);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(CARD_VIEW);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  useEffect(() => {
    dispatch(fetchAnnouncementEvents());
  }, [dispatch]);

  const filtered = items.filter((item) => {
    const byType = typeFilter === 'all' || item.type === typeFilter;
    const effStatus = item.effectiveStatus || computeStatus(item);
    const byStatus = statusFilter === 'all' || effStatus === statusFilter;
    const q = searchQuery.toLowerCase().trim();
    const bySearch = !q || (item.title || '').toLowerCase().includes(q);
    return byType && byStatus && bySearch;
  });

  const totalCount = items.length;
  const announcementsCount = items.filter((i) => i.type === 'announcement').length;
  const eventsCount = items.filter((i) => i.type === 'event').length;
  const ongoingCount = items.filter((i) => (i.effectiveStatus || computeStatus(i)) === 'ongoing').length;

  const handleDelete = async (id) => {
    setMenuAnchor(null);
    if (!window.confirm('Supprimer cette annonce ou cet événement ?')) return;
    setActionLoadingId(id);
    const result = await dispatch(deleteAnnouncementEvent(id));
    setActionLoadingId(null);
    if (deleteAnnouncementEvent.fulfilled.match(result)) {
      enqueueSnackbar('Annonce ou événement supprimé.', { variant: 'success' });
    } else {
      enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
    }
  };

  const getImageUrl = (item) => item?.headlineImage?.url || item?.headlineImage || '';

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* En-tête style premium */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
            Annonces et Événements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez vos {totalCount} annonce{totalCount !== 1 ? 's' : ''} et événement{totalCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/dashboard/announcement-events/new')}
          sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
        >
          Créer une annonce ou un événement
        </Button>
      </Stack>

      {/* Statistiques */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <Typography variant="h4" fontWeight="bold" color="primary.main">{totalCount}</Typography>
          <Typography variant="body2" color="text.secondary">Total</Typography>
        </Paper>
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <Typography variant="h4" fontWeight="bold" color="success.main">{announcementsCount}</Typography>
          <Typography variant="body2" color="text.secondary">Annonces</Typography>
        </Paper>
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <Typography variant="h4" fontWeight="bold" color="info.main">{eventsCount}</Typography>
          <Typography variant="body2" color="text.secondary">Événements</Typography>
        </Paper>
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <Typography variant="h4" fontWeight="bold" color="warning.main">{ongoingCount}</Typography>
          <Typography variant="body2" color="text.secondary">En cours</Typography>
        </Paper>
      </Stack>

      {/* Recherche */}
      <TextField
        size="small"
        fullWidth
        placeholder="Rechercher par titre…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, maxWidth: 560 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Tabs value={typeFilter} onChange={(_, v) => setTypeFilter(v)} sx={{ minHeight: 40 }}>
          <Tab label="Tous" value="all" />
          <Tab label={getTypeLabel('announcement')} value="announcement" />
          <Tab label={getTypeLabel('event')} value="event" />
        </Tabs>
        <TextField
          select
          size="small"
          label="Statut"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">Tous</MenuItem>
          <MenuItem value="published">{getStatusLabel('published')}</MenuItem>
          <MenuItem value="ongoing">{getStatusLabel('ongoing')}</MenuItem>
          <MenuItem value="finished">{getStatusLabel('finished')}</MenuItem>
        </TextField>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
          sx={{ ml: 'auto' }}
        >
          <ToggleButton value={CARD_VIEW} aria-label="Vue cartes">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value={TABLE_VIEW} aria-label="Vue tableau">
            <TableChart />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && items.length === 0 ? (
        <DashboardPageSkeleton variant="cards" />
      ) : (
        <>
          {viewMode === CARD_VIEW && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              {filtered.length === 0 ? (
                <Box gridColumn="1 / -1" sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {items.length === 0 ? 'Aucune annonce ou événement.' : 'Aucun résultat pour cette recherche.'}
                  </Typography>
                </Box>
              ) : (
                filtered.map((item) => {
                  const effStatus = item.effectiveStatus || computeStatus(item);
                  const imgUrl = getImageUrl(item);
                  return (
                    <Card
                      key={item._id}
                      sx={{
                        borderRadius: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardActionArea onClick={() => navigate(`/dashboard/announcement-events/edit/${item._id}`)} sx={{ flex: 1 }}>
                        <CardMedia
                          component="img"
                          height="160"
                          image={imgUrl || '/placeholder.svg'}
                          alt={item.title}
                          sx={{ bgcolor: 'grey.200', objectFit: 'cover' }}
                        />
                        <CardContent sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold" noWrap title={item.title}>
                            {item.title || 'Sans titre'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {formatDateFr(item.startDate)} — {formatDateFr(item.endDate)}
                            {item.place ? ` · ${item.place}` : ''}
                          </Typography>
                          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              label={getStatusLabel(effStatus)}
                              size="small"
                              color={getStatusColor(effStatus)}
                              variant="outlined"
                            />
                            <Chip label={getTypeLabel(item.type)} size="small" variant="outlined" />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                      <Box sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/announcement-events/edit/${item._id}`)}
                          disabled={!!actionLoadingId}
                          title="Modifier"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor(e.currentTarget);
                            setSelectedItem(item);
                          }}
                          disabled={!!actionLoadingId}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  );
                })
              )}
            </Box>
          )}

          {viewMode === TABLE_VIEW && (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxHeight: 520, overflow: 'auto' }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>Titre</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Dates</TableCell>
                    <TableCell>Lieu</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {items.length === 0 ? 'Aucune annonce ou événement.' : 'Aucun résultat pour cette recherche.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((item) => {
                      const effStatus = item.effectiveStatus || computeStatus(item);
                      return (
                        <TableRow key={item._id} hover>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>{getTypeLabel(item.type)}</TableCell>
                          <TableCell>
                            {formatDateFr(item.startDate)} — {formatDateFr(item.endDate)}
                          </TableCell>
                          <TableCell>{item.place || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(effStatus)}
                              size="small"
                              color={getStatusColor(effStatus)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/dashboard/announcement-events/edit/${item._id}`)}
                              disabled={!!actionLoadingId}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setMenuAnchor(e.currentTarget);
                                setSelectedItem(item);
                              }}
                              disabled={!!actionLoadingId}
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => selectedItem && handleDelete(selectedItem._id)}
              disabled={!!actionLoadingId}
            >
              {actionLoadingId === selectedItem?._id ? 'Suppression…' : 'Supprimer'}
            </MenuItem>
          </Menu>
        </>
      )}

      {loading && items.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255,255,255,0.75)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 1,
            borderRadius: 2,
            animation: 'overlayFadeIn 0.2s ease-out',
            '@keyframes overlayFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" fontWeight="500" color="text.secondary">
            Chargement…
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function AnnouncementEventList() {
  const { user } = useSelector((s) => s.auth);
  const allowed = ['admin', 'moderator', 'space-student'].includes(user?.role);
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <AnnouncementEventListContent />;
}
